<script setup lang="ts">
import DialogContentLayout from "@/components/common/DialogContentLayout.vue";
import { ref, reactive, watch, computed, getCurrentInstance } from 'vue'
import customerService from '@/services/customer'
import { getPhoneValidationRules, phoneFormatFilterInternational, phoneRaw as phoneRawUtil } from "@/utils/phone";
const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);

type PhoneForm = {
  phone: string;
  confirmCheck: boolean;
  submitting: boolean;
};

const emit = defineEmits(['update:phone','refresh', 'next']);

const props = defineProps<{
  customerId: number
  phone: string
}>()

const tripReceivedForm = ref()
const showConfirmCheckbox = ref(true)

const phoneForm = reactive<PhoneForm>({
  phone: phoneFormatFilterInternational(props.phone),
  confirmCheck: true,
  submitting: false
})

const phoneRaw = computed(() => phoneRawUtil(phoneForm.phone))

watch(() => props.phone, (val) => {
  if (val !== phoneForm.phone) phoneForm.phone = val
})

const phoneRules = getPhoneValidationRules((key) => t(key).toString())

// Format the phone number on blur using phoneFormatFilterInternational
const onPhoneBlur = (e: Event) => {
  const target = e.target as HTMLInputElement
  const formatted = phoneFormatFilterInternational(target.value)
  phoneForm.phone = formatted
}

const submitPhoneNumber = async (): Promise<void> => {
    const form = tripReceivedForm.value as any
    if (!form.validate()) {
      return
    }

    phoneForm.submitting = true
    try {
        await customerService.updateSMS(props.customerId, {
          phoneNumber: phoneRaw.value,
        })
    } catch (err: any) {
      console.error(`Error updating SMS for customer ${props.customerId} failed: ${err.response.data.message}`)
    }
    phoneForm.submitting = false
    emit('update:phone', phoneForm.phone)
    emit('refresh')
    emit('next')
  }

</script>

<template>
  <DialogContentLayout
    title="Trip Received"
    subtitle="Please confirm or update your cell phone number"
  >
  <div class="margin-t-8">
    <img
            src="@/assets/images/tripReceived.svg"
            class="display-block margin-x-auto max-w-232 margin-t-8"
          />
          <v-form ref="tripReceivedForm">
            <label class="text-left">Cell Phone Number</label>
            <v-text-field
              id="trip-received-cell-phone-number-text-field"
              ref="cell-phone"
              v-model="phoneForm.phone"
              :rules="phoneRules"
              required
              hide-details
              outlined
              placeholder="(888) 888-8888"
              @blur="onPhoneBlur"
            />

            <span v-if="showConfirmCheckbox">
              <v-checkbox
                v-model="phoneForm.confirmCheck"
                label="Receive important trip updates such as driver info, ETAs, and more via text message."
                hide-details
              />
              <v-expand-transition>
                <p v-if="phoneForm.confirmCheck" class="text-gray-light font-12 margin-t-2">
                  By confirming, you agree to CharterUP's
                  <a
                    href="https://www.charterup.com/terms-and-conditions/"
                    target="_blank"
                  >
                    Terms of Service
                  </a>
                  and
                  <a
                    href="https://www.charterup.com/privacy-policy/"
                    target="_blank"
                    class="mr-n1"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </v-expand-transition>
            </span>
            <p v-else class="text-center font-12">Standard message and data rates may apply.</p>
          </v-form>
  </div>
  <template #footer>
    <v-btn
    id="reservation-detail-confirm-phone-number"
    color="primary"
    class="w-full margin-t-6"
    :disabled="!phoneForm.phone"
    :loading="phoneForm.submitting"
    @click="submitPhoneNumber"
  >
    {{ phoneForm.confirmCheck ? 'Get Confirmation Code' : 'Confirm' }}
  </v-btn>
  </template>
  </DialogContentLayout>
</template>
